import { ReactNode, useRef } from 'react';
import type * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { LinkModelNodes, linkModelAtom } from '../../_helpers/ModelLoader/ModelLoader';
import type { ModalModelsStatus } from '..';
import { MainShadow } from './MainShadow';
import { HeadsetModel } from './HeadsetModel';
import { DockWithHubModel } from './DockWithHubModel';
import type { ScrollSceneAnimationControls } from '../../../ThreeLayout/ScrollScene';
import { roundedLerp } from '../../../_utils/roundedLerp';
import { roundedRotationLerp } from '../../../_utils/roundedRotationLerp';
import { round } from '../../../_utils/round';

export type Model = 'Hub' | 'Headset' | 'Dock';

export interface LinkSceneProps {
	sections: {
		carousel: React.RefObject<HTMLDivElement>;
		modal: React.RefObject<HTMLDivElement>;
		dockHover: React.RefObject<HTMLDivElement>;
		headsetHover: React.RefObject<HTMLDivElement>;
	};
	overlines?: {
		dock: ReactNode;
		headset: ReactNode;
	};
	modelsInfo: ModalModelsStatus;
	setNewProductByName(model: Model): void;
}

export default function LinkScene(props: LinkSceneProps & ScrollSceneAnimationControls) {
	const scene = useAtomValue(linkModelAtom);

	const sceneGroupRef = useRef<THREE.Group | null>(null);

	const hover = useRef<SceneProps['hover']>({ hoverObject: 'none', rotation: { x: 0, y: 0 }, scale: 1 }).current;

	useFrame(() => {
		if (!props.modelsInfo.activeProduct && props.scrollSceneAnimationControls.onScrollSceneAnimation.tracker !== 'default') {
			props.scrollSceneAnimationControls.setSmoothPositioning('waiting for start');
			setTimeout(() => {
				props.scrollSceneAnimationControls.resetTrackerToDefault();
				props.scrollSceneAnimationControls.setAllowScrollAnimation(true);
			}, 100);
		}

		if (props.modelsInfo.activeProduct && hover.hoverObject !== 'none') {
			hover.hoverObject = 'none';
			hover.rotation.x = 0;
			hover.rotation.y = 0;
			hover.scale = 0;
		}
	});

	useOnHoverAnimation(hover, sceneGroupRef);
	if (!props.modelsInfo) return null;

	return (
		<group scale={3.75}>
			<group ref={sceneGroupRef} {...{ position: [1, -8.5, 0] }}>
				<HeadsetModel scene={scene} hover={hover} {...props} />
				<DockWithHubModel scene={scene} hover={hover} {...props} />
				<MainShadow scene={scene} hover={hover} {...props} />
			</group>
		</group>
	);
}

export type SceneProps = {
	// sceneGroupRef: React.RefObject<THREE.Group>;
	scene: LinkModelNodes | undefined;
	sections: LinkSceneProps['sections'];
	modelsInfo: LinkSceneProps['modelsInfo'];
	overlines?: LinkSceneProps['overlines'];
	hover: { hoverObject: 'headset' | 'dock' | 'none'; rotation: { x: number; y: number }; scale: number };
	setNewProductByName(model: Model): void;
	learnMore?: {
		learn: string;
		more: string;
	};
};

function useOnHoverAnimation(hover: SceneProps['hover'], sceneGroupRef: React.RefObject<THREE.Group>) {
	useFrame((_, delta) => {
		if (window && sceneGroupRef.current) {
			if (hover.hoverObject !== 'none') {
				/**
				 * scene group scale animation
				 */
				const scaleX = roundedLerp(sceneGroupRef.current.scale.x, hover.scale, delta * 2, 5);
				if (scaleX !== round(sceneGroupRef.current.scale.x, 5)) {
					sceneGroupRef.current.scale.set(scaleX, scaleX, scaleX);
				}
				/**
				 * scene group rotation animation
				 */
				const rotationX = roundedRotationLerp(sceneGroupRef.current.rotation.x, hover.rotation.x, delta * 2, 5);
				if (rotationX !== round(sceneGroupRef.current.rotation.x, 5)) {
					sceneGroupRef.current.rotation.x = rotationX;
				}
				const rotationY = roundedRotationLerp(sceneGroupRef.current.rotation.y, hover.rotation.y, delta * 2, 5);
				if (rotationY !== round(sceneGroupRef.current.rotation.y, 5)) {
					sceneGroupRef.current.rotation.y = rotationY;
				}
			} else {
				/**
				 * scene group scale animation
				 */
				const scaleX = roundedLerp(sceneGroupRef.current.scale.x, 1, delta * 2, 5);
				if (scaleX !== round(sceneGroupRef.current.scale.x, 5)) {
					sceneGroupRef.current.scale.set(scaleX, scaleX, scaleX);
				} else sceneGroupRef.current.scale.set(1, 1, 1);
				/**
				 * scene group rotation animation
				 */
				const rotationX = roundedRotationLerp(sceneGroupRef.current.rotation.x, 0, delta * 2, 5);
				if (rotationX !== round(sceneGroupRef.current.rotation.x, 5)) {
					sceneGroupRef.current.rotation.x = rotationX;
				} else sceneGroupRef.current.rotation.x = 0;

				const rotationY = roundedRotationLerp(sceneGroupRef.current.rotation.y, 0, delta * 2, 5);
				if (rotationY !== round(sceneGroupRef.current.rotation.y, 5)) {
					sceneGroupRef.current.rotation.y = rotationY;
				} else sceneGroupRef.current.rotation.y = 0;
			}
		}
	});
}
